import keycloak from '../keycloak';
import { getStorage, removeStorage, setStorage, type StoredTokenObject } from './authStorage';
import { getJwtExpSeconds } from './jwt';
import { storeTimeoutRedirectPath, triggerSessionTimeout } from './sessionTimeout';
import { destroyRbacSession } from '../rbac/permissionService';

/** Drop the in-memory Keycloak session without navigating away from the current page. */
export function clearKeycloakSessionInMemory(): void {
  try {
    keycloak.clearToken();
  } catch {
    // ignore
  }
}

/** Called only after the user completes an interactive re-login. */
export function clearSessionTimeout(): void {
  removeStorage('timeOut');
}

const DEFAULT_MIN_VALID_SECONDS = 60;

export function isStoredTokenValid(
  storedToken: StoredTokenObject | null | undefined,
  minValidSeconds = DEFAULT_MIN_VALID_SECONDS,
): boolean {
  if (!storedToken?.access_token) return false;
  const exp = storedToken.token_expires_at;
  if (!exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return exp - now > minValidSeconds;
}

export function hasActiveAuthSession(): boolean {
  if (getStorage<boolean>('timeOut') === true) return false;
  if (getStorage<boolean>('justLoggedOut') === true) return false;

  if (keycloak.authenticated && keycloak.token) {
    const exp = keycloak.tokenParsed?.exp ?? getJwtExpSeconds(keycloak.token);
    if (exp != null) {
      const now = Math.floor(Date.now() / 1000);
      return exp - now > 0;
    }
    return true;
  }

  return isStoredTokenValid(getStorage<StoredTokenObject>('token'), 0);
}

export function isSessionTimedOut(): boolean {
  return getStorage<boolean>('timeOut') === true;
}

let invalidating = false;

/** Clear auth state and notify the app to redirect to session timeout / login. */
export function invalidateSession(opts?: { storeRedirect?: boolean }): void {
  if (invalidating) return;
  invalidating = true;
  try {
    if (opts?.storeRedirect !== false) {
      storeTimeoutRedirectPath();
    }
    setStorage('timeOut', true);
    removeStorage('token');
    removeStorage('kcUser');
    removeStorage('appUser');
    destroyRbacSession();
    clearKeycloakSessionInMemory();
    triggerSessionTimeout();
  } finally {
    invalidating = false;
  }
}
